import { useEffect, useState } from "react";
import { X, LogIn, Mail, Loader2, Check, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { startGoogleLogin } from "@/context/AuthContext";
import { fetchEmailAuthConfig, requestMagicLink } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

/**
 * SignInModal — global sign-in dialog offering Google OAuth AND
 * passwordless email magic link. Consumed via `useSignIn()` hook so
 * any component can call `showSignIn(returnPath)`.
 */
export default function SignInModal({ open, onClose, returnPath = "/" }) {
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [mode, setMode] = useState("chooser"); // "chooser" | "email" | "sent"
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;
    fetchEmailAuthConfig().then((c) => {
      if (alive) setEmailEnabled(Boolean(c?.enabled));
    });
    setMode("chooser");
    setEmail("");
    setBusy(false);
    setSentTo("");
  }, [open]);

  if (!open) return null;

  const doGoogle = () => {
    trackEvent("auth_login_start", { provider: "google" });
    startGoogleLogin(returnPath);
  };

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      trackEvent("auth_login_start", { provider: "email" });
      await requestMagicLink({ email: email.toLowerCase().trim(), return_url: returnPath });
      setSentTo(email.toLowerCase().trim());
      setMode("sent");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Could not send sign-in link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9500] bg-black/85 flex items-center justify-center p-4 sm:p-8 backdrop-blur-sm"
      data-testid="signin-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-paper text-ink border-2 border-ink shadow-[8px_8px_0_#1A1C20]"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 border-2 border-ink flex items-center justify-center hover:bg-ink hover:text-paper"
          aria-label="Close"
          data-testid="signin-close-btn"
        >
          <X size={16} />
        </button>

        <div className="border-b-2 border-ink px-6 py-4 bg-paper-alt">
          <div className="font-mono uppercase tracking-[0.3em] text-[10px] text-press-red">
            ✦ TimeCapsule 24 · Sign In
          </div>
        </div>

        <div className="px-6 py-8">
          {mode === "chooser" && (
            <>
              <h2 className="font-headline uppercase font-black text-3xl leading-[0.95] tracking-tighter">
                Welcome back.
              </h2>
              <p className="mt-2 font-body italic text-ink-muted">
                Sign in to save favourites, sync Premium, and pick up any device.
              </p>

              <button
                onClick={doGoogle}
                className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-paper text-ink border-2 border-ink px-5 py-4 font-mono uppercase tracking-widest text-xs hover:bg-ink hover:text-paper"
                data-testid="signin-google-btn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              {emailEnabled && (
                <>
                  <div className="flex items-center gap-3 my-6">
                    <div className="flex-1 h-[2px] bg-ink/20" />
                    <span className="font-mono uppercase tracking-widest text-[10px] text-ink-muted">or</span>
                    <div className="flex-1 h-[2px] bg-ink/20" />
                  </div>

                  <button
                    onClick={() => setMode("email")}
                    className="w-full inline-flex items-center justify-center gap-3 bg-press-red text-paper border-2 border-press-red px-5 py-4 font-mono uppercase tracking-widest text-xs hover:bg-ink hover:border-ink"
                    data-testid="signin-email-btn"
                  >
                    <Mail size={16} />
                    Continue with Email
                  </button>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-ink-muted text-center">
                    Passwordless · We&apos;ll email you a one-tap sign-in link
                  </p>
                </>
              )}
            </>
          )}

          {mode === "email" && (
            <form onSubmit={submitEmail}>
              <button
                type="button"
                onClick={() => setMode("chooser")}
                className="flex items-center gap-1 font-mono uppercase tracking-widest text-[10px] text-ink-muted hover:text-ink mb-4"
                data-testid="signin-email-back-btn"
              >
                <ChevronLeft size={12} /> Back
              </button>
              <h2 className="font-headline uppercase font-black text-3xl leading-[0.95] tracking-tighter">
                Sign in with email.
              </h2>
              <p className="mt-2 font-body italic text-ink-muted">
                Enter your email — we&apos;ll send you a magic link. No password needed.
              </p>

              <label className="block mt-6 font-mono uppercase tracking-widest text-[10px] text-ink-muted mb-1.5">
                Your email
              </label>
              <input
                type="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-paper border-2 border-ink px-4 py-3 font-body focus:outline-none focus:ring-2 focus:ring-press-red"
                data-testid="signin-email-input"
              />

              <button
                type="submit"
                disabled={busy}
                className="mt-5 w-full inline-flex items-center justify-center gap-3 bg-press-red text-paper border-2 border-press-red px-5 py-4 font-mono uppercase tracking-widest text-xs hover:bg-ink hover:border-ink disabled:opacity-60 disabled:cursor-wait"
                data-testid="signin-email-submit-btn"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                {busy ? "Sending…" : "Send Sign-in Link →"}
              </button>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-ink-muted text-center">
                Link expires in 15 minutes · Rate-limited to 3 per 15 min
              </p>
            </form>
          )}

          {mode === "sent" && (
            <div className="text-center py-4" data-testid="signin-email-sent">
              <div className="mx-auto w-16 h-16 border-2 border-ink bg-paper-soft flex items-center justify-center mb-5">
                <Check size={28} className="text-press-red" />
              </div>
              <h2 className="font-headline uppercase font-black text-3xl leading-[0.95] tracking-tighter">
                Check your inbox.
              </h2>
              <p className="mt-3 font-body italic text-ink-muted">
                We sent a sign-in link to<br/>
                <span className="not-italic font-mono text-ink">{sentTo}</span>
              </p>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
                Didn&apos;t get it? Check spam or wait 60s and try again.
              </p>
              <button
                onClick={() => { setMode("email"); setEmail(sentTo); }}
                className="mt-4 font-mono uppercase tracking-widest text-[10px] text-ink-muted underline underline-offset-4 hover:text-ink"
                data-testid="signin-email-resend-btn"
              >
                Send another link
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
